import { PostReach, type OutboxPostingStatusDto } from './outboxStatus';

/**
 * N8 (86d3w2kez) part 2 — the reach-aware recovery rules, as pure functions.
 *
 * <b>Why this exists as its own file.</b> Four surfaces render a failed posting today — the SO card,
 * the CL card, the MIS batch banner and the IR banner — and every one of them had reimplemented the
 * same decision, two of them by hand-rolling `postReach >= 2` instead of calling
 * `mayHaveReachedNetSuite`. The decision they were each re-deriving is the most consequential one in
 * the product: whether pressing a button will DUPLICATE a record in the live company file. Four
 * copies means four chances to get it wrong, and the wrong answer is invisible until a duplicate SO
 * turns up in NetSuite.
 *
 * So the rule lives here once, the four surfaces render it, and it is covered by tests that can be
 * read without a browser.
 *
 * ⚠️ REACH WINS OVER EVERYTHING. `postReach` is sticky and monotonic on the outbox job — it cannot
 * flap back on a later attempt — so once a post may have reached NetSuite, the only safe verb is
 * Reconcile (re-arm → adopt). Re-posting would duplicate the record; cancelling would orphan it. The
 * backend refuses both, but a UI that offers them invites the user to try.
 */

/**
 * What a surface must know to render recovery — deliberately NOT one of the four wire shapes.
 *
 * SO/CL hold an `OutboxPostingStatusDto`, MIS maps a posting batch, and IR maps flat `doc.posting*`
 * fields. Normalising at the call site (a few lines each) is what lets one component serve all four;
 * making this an alias of any one of them would drag that flow's whole DTO into the other three.
 */
export interface PostingRecoveryView {
  /**
   * The outbox job behind this card, when the surface knows it.
   *
   * ⚠️ Used to match N8's `?flash={jobId}` arrival marker, which is the ONLY thing that disambiguates
   * a picklist with several posting batches in flight. Null where a flow cannot name its job — the
   * card still renders, it just cannot be flash-targeted.
   */
  jobId: number | null;

  /** In flight, or dead-lettered. A settled post has no card. */
  status: 'Posting' | 'Failed';

  postReach: PostReach;
  netSuiteRecordId: string | null;
  lastError: string | null;

  /**
   * How this flow names its document HERE, in running prose: "sales order", "cutting list", "MIS",
   * "Item Receipt". Cased by the caller — NetSuite record types are capitalised and warehouse nouns
   * are not, and forcing one rule on both reads wrong somewhere.
   */
  documentNoun: string;

  /**
   * What the record is called ON THE NETSUITE SIDE, when that differs. A cutting list posts as an
   * **Estimate**, and the reach disclosure has to say the word the user will search for over there —
   * "NetSuite accepted the cutting list" sends someone hunting for a record type that does not exist.
   * Defaults to `documentNoun` for the three flows where the two names agree.
   */
  netSuiteNoun?: string;

  /**
   * The FLOW's own identifier for this row, when it is not the outbox job id.
   *
   * ⚠️ MIS needs this and it is not cosmetic. `PostingBatchStatusDto` carries no outbox job id, so
   * `jobId` is null there — but a retry still has to name the right `batchId`, and a picklist can
   * have several batches failing at once. Without it the floating panel would have to recover the
   * batch positionally from the array it was built from, which silently retries the WRONG batch the
   * first time anything reorders or filters that list.
   */
  sourceId?: number | null;

  /** Multi-row flows only — e.g. "Batch 3". Null everywhere else. */
  subLabel?: string | null;
}

/** The verbs a surface may offer. Names match N4's route segments one-for-one. */
export type RecoveryVerb = 'retry' | 'reconcile' | 'cancel';

/**
 * A record MAY (Ambiguous) or DOES (Reached) exist in NetSuite for this failed job.
 *
 * The same predicate as `mayHaveReachedNetSuite`, over the normalised view rather than the SO/CL DTO —
 * which is what lets MIS and IR stop hand-rolling `>= 2`.
 */
export function reachedNetSuite(view: Pick<PostingRecoveryView, 'postReach'>): boolean {
  return view.postReach >= PostReach.Ambiguous;
}

/**
 * ⛔ THE ONE PLACE THAT DECIDES WHICH BUTTONS A FAILED POST OFFERS.
 *
 * Reached (or maybe reached) → Reconcile ALONE. Not "Reconcile plus a disabled Retry", not "Reconcile
 * and Cancel": a cancel here abandons a record NetSuite has already accepted, and a retry duplicates
 * it. Offering either and refusing it server-side is worse than not offering it — the user reads the
 * refusal as a bug and looks for a way around it.
 *
 * Not reached → Retry, and Cancel when the caller supports closing the intent off.
 */
export function recoveryVerbsFor(view: PostingRecoveryView, canCancel: boolean): RecoveryVerb[] {
  if (view.status !== 'Failed') return [];
  if (reachedNetSuite(view)) return ['reconcile'];

  return canCancel ? ['retry', 'cancel'] : ['retry'];
}

/**
 * The reach disclosure — the sentence that tells someone a record may already exist over there.
 *
 * Null when the post demonstrably never reached NetSuite, because there is nothing to disclose and a
 * reassuring line on every failure trains people to skip the one that matters.
 */
export function reachMessageFor(view: PostingRecoveryView): string | null {
  if (view.status !== 'Failed') return null;

  if (view.postReach === PostReach.Reached) {
    const id = view.netSuiteRecordId ? ` (internal id ${view.netSuiteRecordId})` : '';
    const noun = view.netSuiteNoun ?? view.documentNoun;
    return `NetSuite accepted the ${noun}${id}. Reconcile to record it here — do not re-post.`;
  }

  if (view.postReach === PostReach.Ambiguous) {
    return `This post may have reached NetSuite (the response was lost). Reconcile to resolve it safely — do not start a new post.`;
  }

  return null;
}

/**
 * The headline failure line.
 *
 * ⛔ Falls back to naming the document and the flow — never "Something went wrong", which is banned
 * by N8 and tells the reader nothing they can act on.
 */
export function failureMessageFor(view: PostingRecoveryView): string {
  return view.lastError?.trim()
    ? view.lastError
    : `The ${view.documentNoun} could not be posted to NetSuite.`;
}

/** The in-flight reassurance. Same promise on every surface: it finishes without you watching. */
export function inFlightMessageFor(view: PostingRecoveryView): string {
  return `Creating the ${view.documentNoun} in NetSuite in the background — you can keep working; this updates on its own.`;
}

// ── Adapters: four wire shapes → one view ────────────────────────────────────
//
// Each flow reports its posting differently, and these are the ONLY places that difference is
// allowed to exist. Keeping them here rather than in the four components is what stops the next
// person adding a fifth surface from inventing a fifth mapping.

/** SO and CL both hold a full `OutboxPostingStatusDto`. */
export function salesOrderRecoveryView(job: OutboxPostingStatusDto): PostingRecoveryView {
  return {
    jobId: job.id,
    status: job.status,
    postReach: job.postReach,
    netSuiteRecordId: job.netSuiteRecordId,
    lastError: job.lastError,
    documentNoun: 'sales order',
  };
}

/** ⚠️ A cutting list posts to NetSuite AS AN ESTIMATE — the one flow where the two nouns differ. */
export function cuttingListRecoveryView(job: OutboxPostingStatusDto): PostingRecoveryView {
  return {
    jobId: job.id,
    status: job.status,
    postReach: job.postReach,
    netSuiteRecordId: job.netSuiteRecordId,
    lastError: job.lastError,
    documentNoun: 'cutting list',
    netSuiteNoun: 'Estimate',
  };
}

/**
 * One MIS posting BATCH. A picklist can have several in flight at once, each failing independently,
 * which is why the batch keeps its own identity all the way down — a retry has to target the right
 * `batchId`, and `subLabel` is what tells the user which batch a card is about.
 */
export function issuanceBatchRecoveryView(batch: {
  id: number;
  postingJobId?: number | null;
  status: string;
  postReach: number;
  netSuiteRecordId: string | null;
  lastError: string | null;
  batchNumber?: number | null;
}): PostingRecoveryView {
  return {
    // The outbox job id when the batch carries one — today it does NOT, so this is null on MIS.
    // ⛔ The batch id is not a substitute: they live in different tables, and matching a `?flash=`
    // marker against the wrong one silently flashes nothing. Recovery uses `sourceId` instead.
    jobId: batch.postingJobId ?? null,
    sourceId: batch.id,
    status: batch.status === 'Failed' ? 'Failed' : 'Posting',
    postReach: batch.postReach as PostReach,
    netSuiteRecordId: batch.netSuiteRecordId,
    lastError: batch.lastError,
    documentNoun: 'MIS',
    subLabel: batch.batchNumber != null ? `Batch ${batch.batchNumber}` : `Batch ${batch.id}`,
  };
}

/** IR reports posting as flat `doc.posting*` fields rather than a job projection. */
export function itemReceiptRecoveryView(doc: {
  postingJobId?: number | null;
  isFailed: boolean;
  postingReach: number | null;
  postingNetSuiteRecordId: string | null;
  postingLastError: string | null;
}): PostingRecoveryView {
  return {
    jobId: doc.postingJobId ?? null,
    status: doc.isFailed ? 'Failed' : 'Posting',
    // ⚠️ `?? 0` maps a missing reach to Unknown, NOT to NotReached. They are different answers:
    // Unknown means nobody established it, and only NotReached is safe to re-post on.
    postReach: (doc.postingReach ?? PostReach.Unknown) as PostReach,
    netSuiteRecordId: doc.postingNetSuiteRecordId,
    lastError: doc.postingLastError,
    documentNoun: 'Item Receipt',
  };
}
